import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import ClipboardCheckIcon from "@redotech/redo-icons/clipboard-check.svg";
import ClipboardCheckFilledIcon from "@redotech/redo-icons/clipboard-check_filled.svg";
import ClockRewindIcon from "@redotech/redo-icons/clock-rewind_filled.svg";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import InventoryLevelsIcon from "@redotech/redo-icons/inventory_levels.svg";
import ShoppingBagIcon from "@redotech/redo-icons/shopping-bag-01.svg";
import ShoppingBagFilledIcon from "@redotech/redo-icons/shopping-bag-01_filled.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext } from "react";
import { useLocation } from "react-router-dom";

export function useRedoSuppliesNavigation(): PageNavigationEntry | null {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useContext(TeamContext);

  const suppliesEnabled = !!team?.settings?.supplies?.enabled;

  const primaryTab: LinkPrimaryTab = {
    id: "redo-supplies",
    hasNotification: false,
    UnfilledIcon: ClipboardCheckIcon,
    FilledIcon: ClipboardCheckFilledIcon,
    type: "link",
    href: suppliesEnabled
      ? routes.redoSuppliesShopHref
      : routes.redoSuppliesBaseUrl,
    pathRoot: routes.redoSuppliesBaseUrl,
    friendlyName: t("Supplies", {
      context: "packaging and shipping supplies shop navigation label",
    }),
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "redo-supplies",
    name: t("Supplies", {
      context: "packaging and shipping supplies shop navigation label",
    }),
    NavContent: SecondaryRedoSuppliesNavigation,
  };

  return {
    primaryTab,
    secondaryNavigationData,
    isActiveProduct: suppliesEnabled,
  };
}

const SecondaryRedoSuppliesNavigation = memo(
  function SecondaryRedoSuppliesNavigation() {
    const { t } = useTranslation();
    const { id: nodeId, TreeContext } = useNavTreeNode({
      friendlyName: t("Supplies", {
        context: "packaging and shipping supplies shop navigation label",
      }),
    });

    return (
      <TreeContext.Provider value={nodeId}>
        {/* Supplies has no dashboard; Shop is its landing page, so it leads. */}
        <RedoSuppliesShopItem />
        <RedoSuppliesOrderHistoryItem />
        <RedoSuppliesRecurringPurchasesItem />
      </TreeContext.Provider>
    );
  },
);

const RedoSuppliesOrderHistoryItem = memo(
  function RedoSuppliesOrderHistoryItem() {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.redoSuppliesOrderHistoryHref }}
        FilledIcon={ClockRewindIcon}
        friendlyName={t("Order history", {
          context: "packaging and shipping supplies shop navigation label",
        })}
        id="redo-supplies-order-history"
        UnfilledIcon={ClockIcon}
      />
    );
  },
);

const RedoSuppliesRecurringPurchasesItem = memo(
  function RedoSuppliesRecurringPurchasesItem() {
    const { t } = useTranslation();
    const location = useLocation();

    const isRecurringPurchasesActive = location.pathname.includes(
      "/recurring-purchases",
    );

    return (
      <RedoSecondaryNavItem
        action={{ type: "button" }}
        anyChildSelected={isRecurringPurchasesActive}
        FilledIcon={InventoryLevelsIcon}
        friendlyName={t("Recurring purchases", {
          context: "packaging and shipping supplies shop navigation label",
        })}
        id="redo-supplies-recurring-purchases"
        UnfilledIcon={InventoryLevelsIcon}
      >
        <RedoSuppliesRecurringInventoryItem />
        <RedoSuppliesRecurringTimeItem />
      </RedoSecondaryNavItem>
    );
  },
);

const RedoSuppliesRecurringInventoryItem = memo(
  function RedoSuppliesRecurringInventoryItem() {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.redoSuppliesRecurringPurchasesInventoryHref,
        }}
        friendlyName={t("Inventory-based", {
          context:
            "supplies shop navigation label for recurring purchases triggered by inventory levels",
        })}
        id="redo-supplies-recurring-inventory"
      />
    );
  },
);

const RedoSuppliesRecurringTimeItem = memo(
  function RedoSuppliesRecurringTimeItem() {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.redoSuppliesRecurringPurchasesTimeHref,
        }}
        friendlyName={t("Time-based", {
          context:
            "supplies purchase option triggered by a schedule or elapsed time instead of an inventory-level threshold",
        })}
        id="redo-supplies-recurring-time"
      />
    );
  },
);

const RedoSuppliesShopItem = memo(function RedoSuppliesShopItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.redoSuppliesShopHref }}
      FilledIcon={ShoppingBagFilledIcon}
      friendlyName={t("Shop", {
        context: "packaging and shipping supplies shop navigation label",
      })}
      id="redo-supplies-shop"
      UnfilledIcon={ShoppingBagIcon}
    />
  );
});
